/**
 * Till / Store Operations services
 * Handles till registration, store ops, POS config, and cash management.
 */
import axios from "axios";
import dayjs from "dayjs";
import db from "../database";
import useAuthStore from "../stores/useAuthStore";
import { getCurrentTillId } from "../utils/tillIdentity";
import usePOSConfigStore from "../stores/usePOSConfigStore";
import useReferenceStore from "../stores/useReferenceStore";
import useTransactionStore from "../stores/useTransactionStore";
import useSessionStore from "../stores/useSessionStore";

import { applyAuthInterceptors } from "./authInterceptor";
import { applyHealthInterceptors } from "../utils/healthReporter";
import { trace } from "../utils/debugTrace";

const retailURL = process.env.REACT_APP_serverUrl;
const retailInstance = axios.create();
retailInstance.defaults.baseURL = retailURL;
retailInstance.defaults.method = "POST";
retailInstance.defaults.headers.post["Content-Type"] = "application/json";
const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Dynamic tenant baseURL interceptor
retailInstance.interceptors.request.use((config) => {
  const url = window.__CW_TENANT_URLS__?.REACT_APP_serverUrl;
  if (url) config.baseURL = url;
  return config;
});

// Auth interceptors: token attachment + 401 refresh/retry
applyAuthInterceptors(retailInstance);

// Health tracking interceptors (latency + success/failure)
applyHealthInterceptors(retailInstance);

// TSK-5056: Estimate Print Template on the till (backend 52abc82b, column cwr_till.cwr_estimate_template_id).
// Kept separate so the query can be re-sent without it against a backend that doesn't define the field
// yet — GraphQL rejects the whole request on an unknown field, which would otherwise block login.
const ESTIMATE_PRINT_TEMPLATE_FIELDS = `estimatePrintTemplate {
                    cwrPrinttemplateId
                    name
                    htmlcode
                    htmlcode2
                    xmlcode
                    xmlcode2
                    obController
                    cwController
                  }`;
const isUnknownEstimateTemplateField = (resData) =>
  !resData?.data?.data?.tillData && (resData?.data?.errors || []).some((e) => String(e?.message || "").includes("estimatePrintTemplate"));

const syncTillData = async (userData, _authHeaders, includeEstimateTemplate = true) => {
  try {
    const data = {
      query: `query {
            tillData(user: "${userData}") {
              posScanConfigs {
                cwrPosScanConfigId
                scanTrxType
                dataType
                barcodeLength
                startsWith
                endsWith
                customFormula
                calculateTax
                formula
                delimiter
                multiProducts
                qrCode
              }
              loyaltyApply {
                cwrLoyaltyLevelId
                name
                applicableFor
                prodCategories {
                  mProductCategoryId
                  include
                }
              }
              tillAccess {
                cwrTillaccessId
                csClientId
                csUserId
                userAccessController
                cashManagement
                productSync
                salesHistory
                parkedBills
                giftCard
                manualDiscount
                layAway
                dataSyncMonitor
                unlinkTill
                salesReport
                holdBill
                couponCode
                giftCardPreferences
                walletPreferences
                tillAccessMeta {
                  key
                  value
                }
                cwrTill {
                  cwrTillID
                  searchKey
                  till
                  description
                  nextAssignedNumber
                  prefix
                  suffix
                  preorderSequence {
                    sequenceId
                    name
                    prefix
                    suffix
                    nextNo
                  }
                  cancelledOrderSequence {
                    sequenceId
                    name
                    prefix
                    suffix
                    nextNo
                  }
                  loyaltyProgram
                  accessController
                  posType
                  manageCash
                  showopeningamount
                  showsalesamount
                  showexpectedamount
                  showdifferenceamount
                  shiftclose
                  shiftopen
                  eReceipt
                  printPreview
                  cashin
                  cashout
                  layAway
                  payNow
                  calculateTax
                  cashToKeep
                  hardwareController {
                    imageUrl
                    printReceipt
                    weighingScale
                    payment
                    printBarcode
                  }
                  pHWController {
                    imageUrl
                    printReceipt
                    weighingScale
                    payment
                    printBarcode
                  }
                  printTemplate {
                    cwrPrinttemplateId
                    name
                    htmlcode
                    htmlcode2
                    xmlcode
                    xmlcode2
                    obController
                    cwController
                  }
                  tillCloseTemplate {
                    cwrPrinttemplateId
                    name
                    htmlcode
                    htmlcode2
                    xmlcode
                    xmlcode2
                    obController
                  }
                  kotPrintTemplate {
                    cwrPrinttemplateId
                    name
                    htmlcode
                    htmlcode2
                    xmlcode
                    xmlcode2
                  }
                  cancelKotPrintTemplate {
                    cwrPrinttemplateId
                    name
                    htmlcode
                    htmlcode2
                    xmlcode
                    xmlcode2
                  }
                  salesReportTemplate {
                    cwrPrinttemplateId
                    name
                    htmlcode
                    htmlcode2
                    xmlcode
                    xmlcode2
                    obController
                  }
                  storeCloseTemplate {
                    cwrPrinttemplateId
                    name
                    htmlcode
                    htmlcode2
                    xmlcode
                    xmlcode2
                    obController
                    cwController
                  }
                  giftCardPrintTemplate {
                    cwrPrinttemplateId
                    name
                    htmlcode
                    htmlcode2
                    xmlcode
                    xmlcode2
                    obController
                    cwController
                  }
                  creditNotePrintTemplate {
                    cwrPrinttemplateId
                    name
                    htmlcode
                    htmlcode2
                    xmlcode
                    xmlcode2
                    obController
                    cwController
                  }
                  voucherPrintTemplate {
                    cwrPrinttemplateId
                    name
                    htmlcode
                    htmlcode2
                    xmlcode
                    xmlcode2
                    obController
                    cwController
                  }
                  ${includeEstimateTemplate ? ESTIMATE_PRINT_TEMPLATE_FIELDS : ""}
                  enableMultiPrintTemplates
                  printTemplates
                }
                csBunit {
                  csBunitId
                  name
                  value
                  cwrSCustomerId
                  cwrCsDoctypeId
                  cwrPcatalogueId
                  isTaxIncluded
                  allowNegativeStock
                  cwrSpricelistId
                  enableLoyalty
                  csRegionId
                  pCatalogueSaleType {
                    cwrPcatalogueSaletypeId
                    isPromoApplicable
                    cwrSaletypeId
                  }
                  currencies {
                    csCurrencyId
                    currSymbol
                    isoCode
                    prcPrecision
                    stdPrecision
                    cstgPrecision
                    symbolRightSide
                    roundingMethod
                    roundoffForPos
                    denominations {
                      value
                      seqNo
                    }
                    conversions {
                      csCurrencyIDTo
                      currencyFromRate
                      currencyToRate
                      validfromDate
                      validtoDate
                      isoCode
                    }
                  }
                  b2cCustomer {
                    cwrCustomerId
                    sPriceListId
                    code
                    name
                    email
                    mobileNo
                    pincode
                    retlLoyaltyBalance
                    b2cRegisteredstoreId
                    iscredit
                    balancePoints
                    loyaltyLevel {
                      cwrLoyaltyLevelId
                      name
                      accumulationRate
                      redemptionRate
                    }
                    sCustomer {
                      sCustomerID
                      customerCategory {
                        sCustomerCateforyId
                        value
                        name
                        description
                      }
                    }
                  }
                  paymentMethodList {
                    roundOffForPos
                    isReturns
                    cWRPaymentMethodID
                    sequenceNo
                    finPaymentmethodId
                    finFinancialAccountId
                    finDayCloseAccountId
                    name
                    integratedPayment
                    isloyalty
                    paymentProvider
                    iscredit
                    isWallet
                    isGiftCard
                    isCreditNote
                    isGiftVoucher
                    isDefault
                    csCurrencyId
                    isoCode
                    isactive
                    cwrTillId
                    saleTypeName
                    saleTypeId
                    isBankTransfer
                    enablePaymentData
                    paymentDataFields {
                      finPaymentmethodDataFieldId
                      fieldName
                      fieldKey
                      fieldType
                      dataCapture
                      sequence
                      fieldLength
                      isactive
                    }
                    paymentProviderDetails {
                      cwrPaymentProviderId
                      providerName
                      providerType
                      providerCode
                      tid
                      status
                      integrationType
                      providerConfigurations {
                        isSensitive
                        configValue
                        configKey
                      }
                    }
                    salesType {
                      cwrSaletypeId
                      name
                    }
                  }
                  mWarehouse {
                    mWarehouseID
                    name
                  }
                  customerAddress {
                    sCustomerAddressID
                    line1
                    line2
                    line3
                    fulladdress
                    phone
                    city
                    postalcode
                    csCountry {
                      csCountryID
                      name
                    }
                    csRegion {
                      csRegionID
                      name
                    }
                  }
                  locations {
                    csBunitLocationId
                    fulladdress
                    phone
                    contactPerson
                  }
                  salesRep {
                    code
                    name
                    salesRepresentId
                  }
                }
              }
              returnReasons {
                name
                value
                returnReasonId
              }
              dayEndPreferences
              status
              message
            }
          }`,
    };

    const resData = await retailInstance({
      data: data,
    });

    if (includeEstimateTemplate && isUnknownEstimateTemplateField(resData)) {
      console.warn("[syncTillData] backend has no estimatePrintTemplate field yet — retrying without it (estimates fall back to the KOT Print Template)");
      return syncTillData(userData, _authHeaders, false);
    }

    let tillData = resData?.data?.data?.tillData;

    if (tillData?.status === "200") {
      tillData.time = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
      useAuthStore.getState().setTillData(tillData);
      return true;
    } else {
      throw new Error(tillData?.message || "Failed to fetch Till data");
    }
  } catch (error) {
    if (includeEstimateTemplate && isUnknownEstimateTemplateField(error?.response)) {
      console.warn("[syncTillData] backend rejected estimatePrintTemplate — retrying without it (estimates fall back to the KOT Print Template)");
      return syncTillData(userData, _authHeaders, false);
    }
    console.error("Error syncing till data:", error.message);
    return false;
  }
};

const getStoreOps = async (id, date = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss")) => {
  try {
    const queryPayload = {
      query: `query {
                getStoreOps(storeId: "${id}", date: "${date}") {
                    status
                    remarks
                }
            }`,
    };

    const response = await retailInstance({
      data: queryPayload,
    });

    return response;
  } catch (error) {
    console.error("Error in getStoreOps:", error);
    throw error;
  }
};

const tillRegistration = async (syncId, cwrTillID, search_key, timeMark, username) => {
  // TIC-10115: capture the exact request/response around till registration so
  // a recurrence can be diagnosed from __dumpTrace() instead of guessed at —
  // this is the call the backend's TILL_ACTIVE_CHECK guard rejects with
  // "already linked" when a prior session's release never happened.
  trace("TillRegistration", "request", { cwrTillID, search_key, username, syncId, timeMark });
  try {
    const queryPayload = {
      query: `mutation {
            tillRegistration(uniqueId: "${syncId}", tillId: "${cwrTillID}", storeOpsTillId: null,
              till: {
                searchKey: "${search_key}"
                created: "${timeMark}"
                updated: "${timeMark}"
                tillAccess: { csUser: { username: "${username}" } }
                tillHistory: {
                  domain: null
                  sessionid: null
                  osVersion: null
                  localIpAddress: null
                  macAddress: null
                  processor: null
                  ram: null
                  regTimestamp: "${timeMark}"
                }
              }
            ) {
              status
              message
              searchKey
              tillAccess {
                salesRep
                salesRepId
                csClientId
                csUser {
                  csUserId
                }
                cwrTill {
                  till
                  searchKey
                  description
                  nextAssignedNumber
                  prefix
                  suffix
                  preorderSequence {
                    sequenceId
                    name
                    prefix
                    suffix
                    nextNo
                  }
                  cancelledOrderSequence {
                    sequenceId
                    name
                    prefix
                    suffix
                    nextNo
                  }
                }
                csBunit {
                  csBunitId
                  name
                  cwrSCustomerId
                  csCurrency {
                    csCurrencyId
                    currSymbol
                    isoCode
                    stdPrecision
                    cstgPrecision
                    prcPrecision
                  }
                }
              }
              tillHistory {
                domain
                sessionid
                osVersion
                localIpAddress
                macAddress
                processor
                ram
                regTimestamp
                cwrTillRegHistoryId
              }
            }
          }`,
    };

    const response = await retailInstance({
      data: queryPayload,
    });

    const result = response?.data?.data?.tillRegistration;
    const regTraceData = {
      cwrTillID,
      status: result?.status,
      message: result?.message,
      tillAccessCount: result?.tillAccess?.length,
      historyId: result?.tillHistory?.[0]?.cwrTillRegHistoryId,
      username,
    };
    trace("TillRegistration", "response", regTraceData);
    // TIC-10115: ship rejections (status !== "200", e.g. "This Till is already
    // linked to another device") to the Admin observability pipeline as they
    // happen — a local-only trace can be lost to a cache clear before anyone
    // dumps it, this copy is server-side the moment it's rejected.
    if (result?.status && result.status !== "200") {
      window.electronAPI?.observability?.log("error", "till-lifecycle", `Till registration rejected: ${result?.message}`, regTraceData);
    }

    return response;
  } catch (error) {
    const regFailTraceData = {
      cwrTillID,
      message: error?.message,
      httpStatus: error?.response?.status,
      responseBody: error?.response?.data,
      username,
    };
    trace("TillRegistration", "request FAILED", regFailTraceData);
    window.electronAPI?.observability?.log("error", "till-lifecycle", "Till registration request failed", regFailTraceData);
    console.error("Error in tillRegistration:", error);
    throw error;
  }
};

const posConfigService = async (cwrTillID) => {
  try {
    const queryPayload = {
      query: `
              query {
                getPOSConfig(tillId: "${cwrTillID}", name: null) {
                  cwrPosConfigId
                  name
                  posType
                  application
                  configJson
                  PricingRule
                  ExpiryDiscount
                  activateExpiryDiscount
                  registrationTypes
                  feedback
                }
              }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
    console.error("Error in posConfigService:", error);
    throw error;
  }
};

const getTills = async (userid) => {
  try {
    const queryPayload = {
      query: `query {
            getTills(name: null userId: "${userid}") {
              cwrTillID
              till
              searchKey
              cSBunitID
              status
              name
              cSUserId
              userName
              tillHistory {
                cwrTillRegHistoryId
                regTimestamp
                ram
                processor
                sessionid
                macAddress
                domain
                localIpAddress
                csBunitId
              }
            }
          }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
    console.error("Error in getTills:", error);
    throw error;
  }
};

const upsertTill = async (newStringRequest) => {
  try {
    const queryPayload = {
      query: `mutation{
            upsertTill(tillInfo:${newStringRequest})
            {
              status
              message
              cwrTillID
              tillCash{
                cwrTillCashId
                date
                finPaymentmethodId
                opening
                sales
                notes
                netsales
                cashin
                cashout
                retainAmt
                closing
                returns
                iscash
                isclose
                storeDailyOpsTillid
                cashEvents {
                  cwrCashEventsID
                  amount
                  expected
                  diff
                  transactionCount
                  type
                  description
                  cashEventDetails {
                    cwrCashEventdetailsID
                    count
                    amount
                    denomination
                  }
                }
              }
            }
          }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
    console.error("Error in upsertTill", error);
    throw error;
  }
};

const upsertSupplierPayment = async (newStringRequest) => {
  try {
    const queryPayload = {
      query: `mutation {
  upsertSupplierPayment(requests: [
    ${newStringRequest}
  ]) {
    status
    message
  }
}`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
  }
};

const upsertFinFundsTransfer = async (request) => {
  try {
    const payload = {
      query: `
        mutation {
          upsertFinFundsTransfer(requests: [
            ${request}
          ]) {
            status
            message
          }
        }
      `,
    };

    const response = await retailInstance({
      data: payload,
    });
    return response;
  } catch (error) {
    console.error("Bank Transfer Error:", error);
  }
};

const unlinkTill = async (id) => {
  try {
    const queryPayload = {
      query: `mutation {
                    unlinkTill(tillId: "${id}") {
                      status
                      message
                    }
                  }`,
    };
    const response = await retailInstance({ data: queryPayload });
    return response;
  } catch (error) {
    console.error("Error in unlinkTill", error);
  }
};

const upsertPOSActivity = async (tillData, type = "LI") => {
  const tillObj = Array.isArray(tillData) ? tillData[0] : tillData;
  try {
    const queryPayload = {
      query: `mutation {
                                            upsertPOSActivity(tillActivity: [
                                              {
                                                csBunitId: "${tillObj.tillAccess[0].csBunit.csBunitId}"
                                                csUserId: "${tillObj.tillAccess[0].csUser.csUserId}"
                                                tillRegistrationId: "${tillObj.tillHistory[0].cwrTillRegHistoryId}"
                                                type: "${type}"
                                                time: "${dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss")}"
                                              }
                                            ]) {
                                              status
                                              message
                                            }
                                        }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
    console.error("Error in upsertPOSActivity", error);
  }
};

const upsertStoreOps = async (id, userId, date = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss")) => {
  try {
    const mutationPayload = {
      query: `mutation {
                           upsertStoreOps(storeOps: {
                             cSBunitID: "${id}"
                             date: "${date}"
                             status: "OP"
                             closedBy: null
                             closeTime: null
                             openedBy: "${userId}"
                             openTime: "${dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss")}"
                             remarks: null
                           }) {
                             status
                             message
                           }
                         }`,
    };

    const response = await retailInstance({
      data: mutationPayload,
    });

    return response;
  } catch (error) {
    console.error("Error in upsertStoreOps", error);
  }
};

const upsertPOSLogService = async (tillId, userId, bUnitId, dayOpeningArray) => {
  try {
    const queryPayload = {
      query: `
        mutation {
          upsertPOSLog(order: {
            tillId: "${tillId}",
            userId: "${userId}",
            bUnitId: "${bUnitId}",
            lines: [${dayOpeningArray}]
          }) {
            status
            message
          }
        }
      `,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in upsert POS log service");
  }
};

const syncPosConfigData = (syncId, tillId) => {
  const lastUpdatedTime = useSessionStore.getState().lastUpdatedTime;

  return new Promise(async (resolve, reject) => {
    try {
      const paramsInput = {
        query: `query {
              getPOSConfig(tillId: "${tillId}", name: null, lastSyncTime: ${
                lastUpdatedTime ? `"${lastUpdatedTime}"` : null
              }, uniqueId: "${syncId}", storeOpsTillId: null, timeZone:"${timeZone}") {
                cwrPosConfigId
                name
                posType
                application
                configJson
                PricingRule
                ExpiryDiscount
                activateExpiryDiscount
                registrationTypes
                feedback
              }
            }`,
      };

      const response = await retailInstance({
        data: paramsInput,
      });

      const posConfig = response.data.data.getPOSConfig;
      const feedbackData = posConfig[0]?.feedback ? JSON.parse(posConfig[0]?.feedback) : [];

      if (posConfig?.length > 0) {
        const posConfigData = JSON.parse(posConfig[0].configJson);
        const registrationTypes = JSON.parse(posConfig[0].registrationTypes) || [];
        useTransactionStore.getState().setExpiryDiscount(posConfig[0].activateExpiryDiscount);
        usePOSConfigStore.getState().setPosConfig(posConfigData);
        useReferenceStore.getState().setRegistrationTypes(registrationTypes);
        useReferenceStore.getState().setFeedbackJson(feedbackData);
      }
      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

const syncOfflineDta = async (tillData) => {
  try {
    // .where().equals() is a real index query and is fine on both adapters. The
    // trailing .filter() must run after toArray() instead of staying chained on
    // the live collection — chained, it serializes the predicate for IPC, which
    // SQLite mode (CW Mobile Browser) has nothing to execute it and throws.
    const closedEvents = await db.tillEvents.where("tillStatus").equals("closed").toArray();
    const events = closedEvents.filter((event) => !event.isSynced || event.isSynced === 0);

    if (events.length > 0) {
      for (let i = 0; i < events.length; i += 1) {
        const event = events[i];
        const tillSession = useSessionStore.getState().tillSession;
        const tillSessionId = tillSession?.tillSessionId;

        await db.tillEvents
          .where("tillSessionId")
          .equals(event?.tillSessionId)
          .modify((event) => {
            if (tillSessionId !== event?.tillSessionId) {
              event.tillStatus = "closed";
              event.isSynced = 1;
            }
          });

        // Inject openTill field if missing from old stored events
        let tillInfo = events[i].tillInfo;
        if (!tillInfo) {
          console.warn("[syncOfflineDta] Event has no tillInfo — skipping");
          continue;
        }
        if (!tillInfo.includes('"openTill"') && !tillInfo.includes('\\"openTill\\"')) {
          try {
            const parsed = JSON.parse(JSON.parse(tillInfo));
            parsed.openTill = "N";
            tillInfo = JSON.stringify(JSON.stringify(parsed));
          } catch {}
        }

        const paramsInput = {
          query: `mutation{
              upsertTill(tillInfo:${tillInfo})
              {
                status
                message
                cwrTillID
                tillCash{
                  cwrTillCashId
                  date
                  finPaymentmethodId
                  opening
                  sales
                  netsales
                  cashin
                  cashout
                  retainAmt
                  closing
                  returns
                  iscash
                  notes
                  isclose
                  storeDailyOpsTillid
                  cashEvents{
                    cwrCashEventsID
                    amount
                    expected
                    diff
                    transactionCount
                    type
                    description
                    cashEventDetails{
                      cwrCashEventdetailsID
                      count
                      amount
                      denomination
                    }
                  }
                }
              }
            }`,
        };

        try {
          const response = await retailInstance({
            data: paramsInput,
          });

          const result = response.data.data?.upsertTill;
          const status = result?.status;
          if (status === "200") {
            let tillData = event.tillRegistration;
            const queryData = {
              query: `mutation {
                    upsertPOSActivity(tillActivity: [
                      {
                        csBunitId: "${tillData[0]?.tillAccess[0]?.csBunit?.csBunitId}"
                        csUserId: "${tillData[0]?.tillAccess[0]?.csUser?.csUserId}"
                        tillRegistrationId: "${tillData[0]?.tillHistory[0]?.cwrTillRegHistoryId}"
                        type: "LO"
                        time: "${dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss")}"
                      }
                    ]) {
                      status
                      message
                    }
                }`,
            };
            await retailInstance({
              data: queryData,
            });
          }
        } catch (error) {
          console.error("Error syncing offline data:", error.message);
        }
      }
    }
  } catch (error) {
    console.error("Error fetching events from DB:", error.message);
  }
};

const getApprovals = async () => {
  const { v4: uuidv4 } = await import("uuid");
  let tillId = getCurrentTillId();
  let syncId = uuidv4().replace(/-/g, "").toUpperCase();
  await db.POSWorkFlowRules.clear();
  await db.approvers.clear();
  let syncStartTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
  const latestIncrementalSync = await db.dataSyncSummary.where("syncType").equals("Full Sync").reverse().sortBy("syncEndTime");
  try {
    const paramsInput = {
      query: `query {
            getPOSWorkflowRules(tillId:"${tillId}",uniqueId: "${syncId}", storeOpsTillId: null,){
                rules{
                cwrRulesId
               ruleName
               ruleCondition
               rulePriority
                cwrEventId
               eventId
               eventName
               apiType
               apiSource
               apiEndPoint
               triggerType
               }
               approvers {
                   name
                   pin
                   csUserId
                   csBunitId
                   bunitName
                   role
               }
              }
          }`,
    };
    const response = await retailInstance({
      data: paramsInput,
    });
    if (
      response?.data?.data?.getPOSWorkflowRules?.rules !== null &&
      response?.data?.data?.getPOSWorkflowRules?.rules !== undefined &&
      response?.data?.data?.getPOSWorkflowRules?.rules !== ""
    ) {
      await db.POSWorkFlowRules.bulkPut(response?.data?.data?.getPOSWorkflowRules?.rules);
    }

    const approvers = response?.data?.data?.getPOSWorkflowRules?.approvers || [];

    if (approvers.length > 0) {
      const approversWithIds = approvers.map((approver) => ({
        ...approver,
        id: uuidv4(),
        syncId: syncId,
      }));

      await db.approvers.bulkAdd(approversWithIds);
      let syncEndTime = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
      db.dataSyncSummary
        .where("syncId")
        .equals(syncId)
        .modify((event) => {
          event.type = { ...event.type, approvers: { syncStartTime: syncStartTime, syncEndTime, records: approversWithIds.length } };
          event.records = (event.records ? event.records : 0) + approversWithIds.length;
        });
      if (latestIncrementalSync?.length > 0) {
        db.dataSyncSummary
          .where("syncId")
          .equals(latestIncrementalSync[0].syncId)
          .modify((event) => {
            event.type = {
              ...event.type,
              approvers: { incremntalSyncStart: syncStartTime, incremntalSyncEnd: syncEndTime, incremntalSyncRecods: approversWithIds.length, ...event.type?.approvers },
            };
          });
      }
    }
  } catch (error) {
    throw error;
  }
};

const upsertWorkflowEventStore = async (uniqueId, aggregatorType, eventType, exceptionData) => {
  try {
    const queryPayload = {
      query: `mutation {
            upsertWorkflowEventStore(eventStore: [{
               aggregatorId:  "${uniqueId}"
               aggregatorType: "${aggregatorType}"
               eventType: "${eventType}"
               eventData: "${JSON.stringify(exceptionData).replace(/"/g, '\\"')}"
            }]) {
              status
              message
            }
          }`,
    };
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in upsertWorkflowEventStore");
  }
};

const transactionQueue = async (query) => {
  const queryPayload = {
    query,
  };
  try {
    const response = await retailInstance({
      data: queryPayload,
    });
    return response;
  } catch (error) {
    console.error(error, "Error in transaction queue");
  }
};

/**
 * Fetches aggregator (Swiggy/Zomato) sales data for a given business unit and date.
 * Used during till close to include aggregator totals in the receipt.
 *
 * @param {string} bunitId - The business unit ID
 * @param {string} date - The date string (YYYY-MM-DD) for the sales query
 * @returns {Object|Array} Parsed aggregator data or empty array on failure
 */
const getAggregatorSalesData = async (bunitId, date) => {
  try {
    const queryPayload = {
      query: `query {
        getAggregatorSalesData(
          bunitId: "${bunitId}",
          date: "${date}"
        )
      }`,
    };

    const response = await retailInstance({ data: queryPayload });
    const rawData = response?.data?.data?.getAggregatorSalesData;

    const aggregatorData = typeof rawData === "string" ? JSON.parse(rawData) : rawData;

    return aggregatorData;
  } catch (error) {
    console.error("Error fetching aggregator sales data:", error);
    return [];
  }
};

export {
  syncTillData,
  getStoreOps,
  tillRegistration,
  posConfigService,
  getTills,
  upsertTill,
  upsertSupplierPayment,
  upsertFinFundsTransfer,
  unlinkTill,
  upsertPOSActivity,
  upsertStoreOps,
  upsertPOSLogService,
  syncPosConfigData,
  syncOfflineDta,
  getApprovals,
  upsertWorkflowEventStore,
  transactionQueue,
  getAggregatorSalesData,
};
